import type { ReactElement } from "react";
import { Chip, type ChipProps } from "@mui/material";
import { roleLabel } from "./formatters.js";

/**
 * Role chip — outlined, no fill, uniform treatment for every role.
 * Per UX brief §3.2 the role column is a "borderless lookup"; we use
 * MUI Chip variant=outlined for the row look.
 *
 * Canonical 7-role palette (auth/v1@4.3.0 / Story 054a / BE-058):
 *
 *   - `tenant_admin`     → `primary`  (Signal Blue, brand accent — the
 *                                      tenant-side admin role)
 *   - `project_manager`  → `primary`  (shares with tenant_admin — both
 *                                      are tenant-side coordinator roles)
 *   - `expert_analyst`   → default    (neutral outline — the analytical
 *                                      workhorse role)
 *   - `pre_screener`     → default    (first-pass analyst sibling)
 *   - `back_office`      → default    (non-admin operational role)
 *   - `pilot`            → default    (field-execution role)
 *   - `platform_admin`   → default    (rare on the tenant surface; not
 *                                      selectable from invite UI)
 *
 * The "no new tokens" constraint kept this on MUI's existing semantic
 * slots rather than reaching into the @aeriosiq/ui token table.
 */
const ROLE_CHIP_COLOR: Record<string, ChipProps["color"]> = {
  tenant_admin: "primary",
  project_manager: "primary",
};

export function RoleChip({ role }: { role: string }): ReactElement {
  const color = ROLE_CHIP_COLOR[role] ?? "default";
  return (
    <Chip
      label={roleLabel(role)}
      variant="outlined"
      size="small"
      color={color}
    />
  );
}
